import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { mapBackendError } from "@/i18n/errorMap";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, User, Mail, Lock, Sparkles } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";
import { trackSignUp, trackStartTrialFree } from "@/lib/analytics";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const t = useT();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await register(email, password, name, window.location.origin);
      const trialGranted = Boolean(data?.trial_granted);
      const userId = data?.id || data?.user?.id;
      if (userId) trackSignUp({ signupKey: `user:${userId}` }).catch(() => {});
      if (trialGranted && userId) trackStartTrialFree({ userId }).catch(() => {});
      if (trialGranted) {
        toast.success(t("register.successTrial"));
        navigate("/", { replace: true });
      } else {
        toast.info(t("register.successNoTrial"));
        navigate("/subscribe?reason=trial_used", { replace: true });
      }
    } catch (err) {
      setError(mapBackendError(err.response?.data?.detail, t) || err.message);
      setBusy(false);
    }
  };

  const fieldCls = "pl-10 h-11 rounded-sm bg-white/[0.04] border-white/[0.1] text-white placeholder:text-white/25 font-geist focus-visible:ring-[#4D8AFF] focus-visible:border-[#4D8AFF]/50";
  const labelCls = "font-geist-mono text-[10px] uppercase tracking-[0.18em] text-white/50";

  return (
    <AuthShell
      step={t("auth.stepRegister")}
      title={t("register.title")}
      subtitle={t("register.subtitle")}
    >
      <form onSubmit={submit} data-testid="register-form" className="space-y-5">
        <div>
          <Label htmlFor="name" className={labelCls}>{t("register.nameLabel")}</Label>
          <div className="relative mt-2">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
            <Input
              id="name"
              data-testid="register-name-input"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("register.namePlaceholder")}
              autoComplete="name"
              maxLength={100}
              pattern="^(?!.*(pk|sk|rk|whsec)_(test|live)_).{1,100}$"
              className={fieldCls}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="email" className={labelCls}>{t("register.emailLabel")}</Label>
          <div className="relative mt-2">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
            <Input
              id="email"
              data-testid="register-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder={t("register.emailPlaceholder")}
              className={fieldCls}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="password" className={labelCls}>{t("register.passwordLabel")}</Label>
          <div className="relative mt-2">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
            <PasswordInput
              id="password"
              data-testid="register-password-input"
              toggleTestId="register-password-toggle"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder={t("register.passwordPlaceholder")}
              className={fieldCls}
            />
          </div>
        </div>

        {error && (
          <div
            data-testid="register-error"
            className="font-geist-mono text-[11px] text-red-300 border border-red-400/30 bg-red-500/10 rounded-sm px-3 py-2"
          >{error}</div>
        )}

        <Button
          data-testid="register-submit-btn"
          type="submit"
          disabled={busy}
          className="w-full h-11 rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] font-geist font-semibold tracking-tight group transition-colors"
        >
          {busy ? (
            <span className="font-geist-mono text-xs uppercase tracking-wider">{t("register.submitting")}</span>
          ) : (
            <span className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> {t("register.submit")}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </span>
          )}
        </Button>
        <p className="font-geist-mono text-[10px] text-white/40 text-center leading-relaxed pt-1">
          <strong className="text-white/70">{t("register.footerTagline")}</strong><br />
          {t("register.footerDetail")}
        </p>
      </form>

      <div className="mt-6 pt-6 border-t border-white/[0.08] flex items-center justify-between font-geist-mono text-[11px] text-white/40">
        <span>{t("register.haveAccount")}</span>
        <Link
          to="/login"
          data-testid="go-to-login-link"
          className="text-[#4D8AFF] hover:text-white transition-colors uppercase tracking-wider"
        >{t("register.toLogin")}</Link>
      </div>
    </AuthShell>
  );
}
